import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Coffee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_brew-business-value/artifacts/t1741byz_Colour%20Landscape%20Logo.png";

const QuestionnaireWizard = ({ questions, initialAnswers, onComplete, onBack }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [answers, setAnswers] = useState(initialAnswers || {});
    const [direction, setDirection] = useState(1);

    const currentQuestion = questions[currentIndex];
    const progress = ((currentIndex + 1) / questions.length) * 100;
    const isLastQuestion = currentIndex === questions.length - 1;
    const currentAnswer = answers[currentQuestion.id];

    // Get section color
    const getSectionColor = (section) => {
        switch (section) {
            case "Operational Metrics":
                return "bg-blue-500";
            case "Financial Inputs":
                return "bg-green-500";
            case "Business Stability":
                return "bg-purple-500";
            case "Asset Value":
                return "bg-orange-500";
            default:
                return "bg-primary";
        }
    };

    const handleAnswer = (value) => {
        setAnswers(prev => ({
            ...prev,
            [currentQuestion.id]: value
        }));
    };

    const handleNext = () => {
        if (isLastQuestion) {
            onComplete(answers);
        } else {
            setDirection(1);
            setCurrentIndex(prev => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (currentIndex === 0) {
            onBack();
        } else {
            setDirection(-1);
            setCurrentIndex(prev => prev - 1);
        }
    };

    const canProceed = (currentQuestion.type === "number" || currentQuestion.type === "currency")
        ? currentAnswer !== undefined && currentAnswer !== "" && !isNaN(currentAnswer) && Number(currentAnswer) >= 0
        : currentAnswer !== undefined && currentAnswer !== "";

    const slideVariants = {
        enter: (direction) => ({
            x: direction > 0 ? 100 : -100,
            opacity: 0
        }),
        center: {
            x: 0,
            opacity: 1
        },
        exit: (direction) => ({
            x: direction > 0 ? -100 : 100,
            opacity: 0
        })
    };

    return (
        <div className="min-h-screen bg-surface flex flex-col" data-testid="questionnaire-wizard">
            {/* Header */}
            <header className="w-full py-4 px-6 bg-white border-b border-gray-100">
                <div className="max-w-3xl mx-auto flex items-center justify-between">
                    <img 
                        src={LOGO_URL} 
                        alt="Bonza" 
                        className="h-10 object-contain"
                    />
                    <div className="flex items-center gap-2 text-sm text-text-secondary">
                        <Coffee className="w-4 h-4 text-primary" />
                        <span>Café Valuation</span>
                    </div>
                </div>
            </header>

            {/* Progress Section */}
            <div className="w-full py-4 px-6 bg-white border-b border-gray-100">
                <div className="max-w-3xl mx-auto space-y-2">
                    <div className="flex items-center justify-between text-sm">
                        <span className="text-text-muted">
                            Question {currentIndex + 1} of {questions.length}
                        </span>
                        <span className="font-medium text-primary">
                            {Math.round(progress)}% complete
                        </span>
                    </div>
                    <Progress value={progress} className="h-2" />
                </div>
            </div>

            {/* Question Content */}
            <main className="flex-1 flex items-center justify-center py-8 px-6">
                <div className="w-full max-w-xl">
                    <AnimatePresence mode="wait" custom={direction}>
                        <motion.div
                            key={currentIndex}
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="space-y-8"
                        >
                            {/* Section Badge */}
                            <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full ${getSectionColor(currentQuestion.section)}`} />
                                <span className="text-sm font-medium text-text-muted uppercase tracking-wide">
                                    {currentQuestion.section}
                                </span>
                            </div>

                            {/* Question Card */}
                            <div className="bg-white rounded-2xl p-8 shadow-card border border-gray-100">
                                <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                                    {currentQuestion.question}
                                </h2>

                                {currentQuestion.helperText && (
                                    <p className="text-text-muted mb-6">
                                        {currentQuestion.helperText}
                                    </p>
                                )}

                                {/* Input based on type */}
                                {currentQuestion.type === "number" ? (
                                    <div className="relative">
                                        <Input
                                            type="number"
                                            value={currentAnswer || ""}
                                            onChange={(e) => handleAnswer(e.target.value === "" ? "" : Number(e.target.value))}
                                            placeholder={currentQuestion.placeholder}
                                            className="w-full text-lg py-6 px-4 border-2 border-gray-200 focus:border-primary rounded-xl pr-20"
                                            min="0"
                                            step="1"
                                            data-testid={`input-${currentQuestion.id}`}
                                        />
                                        {currentQuestion.suffix && (
                                            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted">
                                                {currentQuestion.suffix}
                                            </span>
                                        )}
                                    </div>
                                ) : currentQuestion.type === "currency" ? (
                                    <div className="relative">
                                        <span className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted text-lg">$</span>
                                        <Input
                                            type="number"
                                            value={currentAnswer || ""}
                                            onChange={(e) => handleAnswer(e.target.value === "" ? "" : Number(e.target.value))}
                                            placeholder={currentQuestion.placeholder}
                                            className="w-full text-lg py-6 pl-8 pr-4 border-2 border-gray-200 focus:border-primary rounded-xl"
                                            step="1000"
                                            data-testid={`input-${currentQuestion.id}`}
                                        />
                                    </div>
                                ) : (
                                    <Select
                                        value={currentAnswer || ""}
                                        onValueChange={handleAnswer}
                                    >
                                        <SelectTrigger 
                                            className="w-full text-lg py-6 px-4 border-2 border-gray-200 focus:border-primary rounded-xl h-auto"
                                            data-testid={`select-${currentQuestion.id}`}
                                        >
                                            <SelectValue placeholder="Select an option" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {currentQuestion.options.map((option) => (
                                                <SelectItem 
                                                    key={option} 
                                                    value={option}
                                                    className="text-lg py-3"
                                                    data-testid={`option-${option}`}
                                                >
                                                    {option}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                            </div>

                            {/* Navigation Buttons */}
                            <div className="flex items-center justify-between gap-4">
                                <Button
                                    variant="outline"
                                    onClick={handlePrevious}
                                    className="flex items-center gap-2 px-6 py-3 rounded-xl border-2 hover:bg-gray-50"
                                    data-testid="prev-btn"
                                >
                                    <ArrowLeft className="w-4 h-4" />
                                    <span className="hidden sm:inline">Back</span>
                                </Button>

                                <Button
                                    onClick={handleNext}
                                    disabled={!canProceed}
                                    className={`flex items-center gap-2 px-8 py-3 rounded-xl font-semibold transition-all duration-300 ${
                                        canProceed 
                                            ? "bg-primary hover:bg-primary/90 text-white shadow-lg shadow-primary/30 hover:-translate-y-0.5" 
                                            : "bg-gray-200 text-gray-400 cursor-not-allowed"
                                    }`}
                                    data-testid="next-btn"
                                >
                                    {isLastQuestion ? "Get My Valuation" : "Next"}
                                    <ArrowRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </main>

            {/* Footer */}
            <footer className="w-full py-4 px-6 bg-white border-t border-gray-100">
                <div className="max-w-3xl mx-auto flex items-center justify-center">
                    <p className="text-sm text-text-muted">
                        Your information is kept confidential and secure
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default QuestionnaireWizard;
